import { useTranslation } from 'react-i18next'
import { CheckCircle2 } from 'lucide-react'
import { Logo } from '../components/Logo'
import { useTenant } from '../context/TenantContext'

/**
 * Shown when a patient opens a magic link that is spent or expired AND their
 * registration is already on file — or a NEW link although they completed the
 * form within the last 90 days (server flag `alreadyCompleted`, 2026-10-29).
 *
 * Before this, that patient got a red "link already used — contact the practice"
 * toast and an empty form: an error message for someone who had done nothing
 * wrong and had nothing left to do. Reassurance is the whole job of this screen.
 *
 * "Fill in again" stays available for the patient who genuinely needs to resubmit
 * (it only reveals the form; for a spent link it grants no session, so an org
 * enforcing magic links still needs a fresh one — a new link's session stays valid).
 */
export function AlreadyCompletedPage({ onFillAgain }: { onFillAgain: () => void }) {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const contactEmail = tenant?.contactEmail

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <div className="form-container flex-1">
        <div className="form-inner py-12">
          <div className="flex justify-center mb-8">
            <Logo size={150} />
          </div>

          <div className="flex justify-center mb-6">
            <span className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </span>
          </div>

          <h1 className="text-h1 text-foreground mb-4 text-center">
            {t('alreadyCompleted.title')}
          </h1>

          <p className="text-body text-muted-foreground mb-2 max-w-md mx-auto text-center">
            {t('alreadyCompleted.body')}
          </p>
          <p className="text-body text-muted-foreground mb-8 max-w-md mx-auto text-center">
            {t('alreadyCompleted.noAction')}
          </p>

          {contactEmail && (
            <p className="text-small text-muted-foreground text-center mb-8">
              {t('alreadyCompleted.questions')}<br />
              <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                {contactEmail}
              </a>
            </p>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={onFillAgain}
              className="text-small text-muted-foreground underline hover:text-foreground"
            >
              {t('alreadyCompleted.fillAgain')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
